import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Download, Image as ImageIcon, Search, Filter, Eye, Loader2, RefreshCw, ArrowLeft, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSecureAuthContext } from '@/contexts/SecureAuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import ImagePopup from '@/components/ImagePopup';
import GalleryDownloadManager from '@/components/GalleryDownloadManager';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

interface GeneratedImageRecord {
  id: string;
  prompt: string;
  image_url: string;
  b64_json?: string;
  model: string;
  size: string;
  quality: string;
  style: string;
  created_at: string;
  batch_id: string;
  original_settings?: any;
}

const CompleteGallery = () => {
  const { user } = useSecureAuthContext();
  const [images, setImages] = useState<GeneratedImageRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [modelFilter, setModelFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [selectedImages, setSelectedImages] = useState<GeneratedImageRecord[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [cursor, setCursor] = useState<string | null>(null);
 
  const ITEMS_PER_PAGE = 12; // Reduced further to minimize load
  const DEFAULT_DAYS_BACK = 90;

  useEffect(() => {
    if (user?.id) {
      fetchImages(true);
      fetchTotalCount();
    }
  }, [user?.id, modelFilter, sortBy]);

  const fetchTotalCount = async () => {
    try {
      let query = supabase
        .from('generated_images')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user?.id);

      if (modelFilter !== 'all') {
        query = query.eq('model', modelFilter);
      }

      const { count, error } = await query;
      if (error) throw error;
      setTotalCount(count || 0);
    } catch (error) {
      console.error('Error fetching total count:', error);
    }
  };

  const fetchImages = async (reset = false) => {
    try {
      if (reset) {
        setLoading(true);
        setImages([]);
        setHasMore(true);
        setCursor(null);
      } else {
        setLoadingMore(true);
      }

      if (!user?.id) {
        console.error('No user ID available');
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      // Use range-based pagination instead of offset
      const since = new Date(Date.now() - DEFAULT_DAYS_BACK * 24 * 60 * 60 * 1000).toISOString();
      let query = supabase
        .from('generated_images')
        .select('id, prompt, image_url, model, size, quality, style, created_at, batch_id')
        .eq('user_id', user.id)
        .not('image_url', 'is', null)
        .gte('created_at', since)
        .order('created_at', { ascending: sortBy === 'oldest' })
        .limit(ITEMS_PER_PAGE);
 
      if (modelFilter !== 'all') {
        query = query.eq('model', modelFilter);
      }
 
      // Keyset pagination (no offsets)
      if (!reset && cursor) {
        if (sortBy === 'newest') {
          query = query.lt('created_at', cursor);
        } else {
          query = query.gt('created_at', cursor);
        }
      }

      console.log('Fetching images with range-based pagination...');
      const { data, error } = await query;

      if (error) {
        console.error('Supabase error details:', error);
        throw error;
      }

      console.log('Fetched images:', data?.length || 0);

      if (data) {
        if (reset) {
          setImages(data);
        } else {
          setImages(prev => [...prev, ...data]);
        }
        if (data.length > 0) {
          const lastItem = data[data.length - 1];
          setCursor(lastItem.created_at);
        }
        setHasMore(data.length === ITEMS_PER_PAGE);
      } else {
        setHasMore(false);
      }

    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error(`Failed to load images: ${error.message || 'Unknown error'}`);
      setHasMore(false);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMore = async () => {
    if (!loadingMore && hasMore && !loading) {
      await fetchImages(false);
    }
  };

  const filteredImages = images.filter(image => 
    image.prompt.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const downloadImage = async (imageUrl: string, prompt: string, imageB64?: string) => {
    try {
      // Detect MIME type from base64
      const detectMime = (b64: string) => {
        if (b64.startsWith('/9j/')) return 'image/jpeg';
        if (b64.startsWith('iVBORw')) return 'image/png';
        return 'image/jpeg';
      };
      const getExt = (mime: string) => mime.includes('jpeg') ? 'jpg' : 'png';
      
      let finalImageUrl = imageUrl;
      let mimeType = 'image/jpeg';
      
      if (imageB64) {
        mimeType = detectMime(imageB64);
        finalImageUrl = `data:${mimeType};base64,${imageB64}`;
      } else if (imageUrl && imageUrl.startsWith('data:image/')) {
        mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
      }
      
      const imgResponse = await fetch(finalImageUrl);
      const imgBlob = await imgResponse.blob();
      const url = window.URL.createObjectURL(imgBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${prompt.slice(0, 30).replace(/[^a-z0-9]/gi, '_')}-${Date.now()}.${getExt(mimeType)}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Image downloaded successfully!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardContent className="p-6">
            <div className="text-center text-gray-400 py-12">
              <ImageIcon className="w-12 h-12 mx-auto mb-4" />
              <p className="text-white">Please sign in to view your gallery</p>
              <Link to="/auth">
                <Button className="mt-4">Sign In</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:50px_50px]"></div>
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-purple-600/10"></div>
      </div>

      {/* Header */}
      <header className="bg-black/20 backdrop-blur-xl border-b border-white/10 relative z-10">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/dashboard">
                <Button variant="ghost" className="text-white hover:bg-white/10">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Dashboard
                </Button>
              </Link>
              <h1 className="text-2xl font-bold text-white">Complete Gallery</h1>
            </div>
            <Link to="/">
              <Button variant="ghost" className="text-white hover:bg-white/10">
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 relative z-10 space-y-8">
        {/* Download Manager */}
        {totalCount > 0 && (
          <GalleryDownloadManager 
            images={filteredImages} 
            onSelectionChange={setSelectedImages}
          />
        )}

        {/* Gallery */}
        <Card className="bg-white/10 backdrop-blur-md border-white/20">
          <CardHeader>
            <div className="flex justify-between items-start">
              <CardTitle className="text-white flex items-center">
                <ImageIcon className="w-6 h-6 mr-2" />
                Your Complete Gallery ({totalCount} total images, showing {filteredImages.length})
              </CardTitle>
              
              <Button
                onClick={() => fetchImages(true)}
                disabled={loading}
                className="bg-green-600 hover:bg-green-700"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
            
            {/* Filters and Search */}
            <div className="flex flex-col sm:flex-row gap-4 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input
                  placeholder="Search prompts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
                />
              </div>
              
              <Select value={modelFilter} onValueChange={setModelFilter}>
                <SelectTrigger className="w-48 bg-white/10 border-white/20 text-white">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Models</SelectItem>
                  <SelectItem value="dall-e-2">DALL-E 2</SelectItem>
                  <SelectItem value="dall-e-3">DALL-E 3</SelectItem>
                  <SelectItem value="gpt-image-1">GPT Image 1</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="newest">Newest</SelectItem>
                  <SelectItem value="oldest">Oldest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          
          <CardContent>
            {loading ? (
              <div className="text-center text-gray-400 py-12">
                <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin" />
                <p className="text-white">Loading your gallery...</p>
              </div>
            ) : filteredImages.length === 0 ? (
              <div className="text-center text-gray-400 py-12">
                <ImageIcon className="w-12 h-12 mx-auto mb-4" />
                <p className="text-white">No images found</p>
                <p className="text-sm text-gray-400 mt-2">
                  {searchTerm || modelFilter !== 'all' 
                    ? 'Try adjusting your search or filters' 
                    : 'Start generating your first images'}
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {filteredImages.map((image) => (
                    <div key={image.id} className="relative group">
                      <div className="bg-white/5 rounded-lg overflow-hidden hover:bg-white/10 transition-all duration-300">
                        <div className="relative">
                          <img
                            src={(() => {
                              if (image.b64_json) {
                                const mime = image.b64_json.startsWith('/9j/') ? 'image/jpeg' : 
                                             image.b64_json.startsWith('iVBORw') ? 'image/png' : 'image/jpeg';
                                return `data:${mime};base64,${image.b64_json}`;
                              }
                              return image.image_url || '';
                            })()}
                            alt={image.prompt}
                            className="w-full h-48 object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                            <ImagePopup 
                              image={image} 
                              onCopySettings={() => {}}
                            >
                              <Button size="sm" className="bg-white/20 hover:bg-white/30">
                                <Eye className="w-4 h-4 mr-1" />
                                View
                              </Button>
                            </ImagePopup>
                            <Button
                              size="sm"
                              onClick={() => downloadImage(image.image_url, image.prompt, image.b64_json)}
                              className="bg-white/20 hover:bg-white/30"
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </div>
                        </div>
                        <div className="p-3">
                          <p className="text-white text-xs mb-2 line-clamp-2 leading-relaxed">{image.prompt}</p>
                          <div className="flex items-center justify-between text-xs mb-1">
                            <span className="text-blue-400 font-medium">{image.model}</span>
                            <span className="text-gray-400">{image.size}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-gray-400">{formatDate(image.created_at)}</span>
                            <span className="text-gray-400">{image.quality}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Load More Button */}
                {hasMore && (
                  <div className="text-center mt-8">
                    <Button
                      onClick={loadMore}
                      disabled={loadingMore}
                      variant="outline"
                      className="bg-white/10 border-white/20 text-white hover:bg-white/20"
                    >
                      {loadingMore ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        'Load More'
                      )}
                    </Button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default CompleteGallery;
